/**
 * useSortableArticles - Custom hook for drag-and-drop article sorting
 *
 * Provides @dnd-kit integration with minimal changes to existing components.
 * Uses separate SortableContexts per category with droppable zones for cross-category moves.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  pointerWithin,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

// LexoRank utilities for position calculation
const LEXORANK_BASE = 36;
const LEXORANK_LENGTH = 6;

function toInt(str) {
  return parseInt(str, LEXORANK_BASE);
}

function fromInt(num, length = LEXORANK_LENGTH) {
  return num.toString(LEXORANK_BASE).padStart(length, '0');
}

export function initialPosition() {
  return 'm00000';
}

export function positionAfter(position) {
  const len = position?.length || LEXORANK_LENGTH;
  const num = toInt(position || initialPosition());
  const max = Math.pow(LEXORANK_BASE, len) - 1;
  const mid = Math.floor((num + max) / 2);
  return fromInt(mid, len);
}

export function positionBefore(position) {
  const len = position?.length || LEXORANK_LENGTH;
  const num = toInt(position || initialPosition());
  const mid = Math.floor(num / 2);
  return fromInt(mid, len);
}

export function positionBetween(a, b) {
  const len = Math.max(a?.length || LEXORANK_LENGTH, b?.length || LEXORANK_LENGTH);
  const ai = toInt((a || '').padEnd(len, '0'));
  const bi = toInt((b || 'zzzzzz').padEnd(len, '0'));
  const mid = Math.floor((ai + bi) / 2);

  if (mid === ai || mid === bi) {
    return null;
  }

  return fromInt(mid, len);
}

/**
 * Calculate new position for an item
 */
export function calculateNewPosition(items, targetIndex, excludeId = null) {
  // Filter out the item being moved
  const filteredItems = excludeId
    ? items.filter(item => item.id !== excludeId)
    : items;

  // Sort by position
  const sortedItems = [...filteredItems].sort((a, b) =>
    (a.position || '').localeCompare(b.position || '')
  );

  // Inserting at start
  if (targetIndex <= 0) {
    if (sortedItems.length === 0) {
      return initialPosition();
    }
    return positionBefore(sortedItems[0].position);
  }

  // Inserting at end
  if (targetIndex >= sortedItems.length) {
    return positionAfter(sortedItems[sortedItems.length - 1]?.position);
  }

  // Inserting between two items
  const prevItem = sortedItems[targetIndex - 1];
  const nextItem = sortedItems[targetIndex];

  const newPos = positionBetween(prevItem?.position, nextItem?.position);
  if (newPos === null) {
    // Fallback
    return positionAfter(prevItem?.position);
  }

  return newPos;
}

/**
 * Sortable item wrapper component
 * @param {string} id - Item ID
 * @param {string} category - Category this item belongs to
 * @param {string} overCategory - Category currently being hovered over (for cross-category visual feedback)
 * @param {React.ReactNode} children - Render prop function
 */
export function SortableItem({ id, category, overCategory, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    data: { category, type: 'item' },
  });

  const style = {
    // Apply transform only when not dragging (for reordering animations)
    transform: isDragging ? undefined : CSS.Transform.toString(transform),
    transition,
    // Completely hide the original when dragging - DragOverlay shows the ghost
    opacity: isDragging ? 0 : 1,
    visibility: isDragging ? 'hidden' : 'visible',
    // Keep height so the space is reserved for drop target
    pointerEvents: isDragging ? 'none' : 'auto',
  };

  return children({
    ref: setNodeRef,
    style,
    attributes,
    listeners,
    isDragging,
  });
}

/**
 * Droppable category zone - makes the category a drop target for cross-category moves
 */
export function DroppableCategory({ category, itemIds, children }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `category-${category}`,
    data: { category, type: 'category' },
  });

  return (
    <div ref={setNodeRef}>
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        {children({ isOver })}
      </SortableContext>
    </div>
  );
}

// Keep old name for backwards compatibility
export const SortableCategory = DroppableCategory;

/**
 * Main hook for sortable articles
 *
 * Uses the dnd-kit pattern for multi-container sorting:
 * - Maintains local state copy of items during drag
 * - Moves items between containers in onDragOver (not just onDragEnd)
 * - This provides correct visual feedback during cross-container drags
 */
export function useSortableArticles({
  groupedItems: externalGroupedItems,
  onReorder,
  onCategoryChange,
}) {
  const [activeId, setActiveId] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null); // Original category when drag started
  const [overCategory, setOverCategory] = useState(null);
  const [activeWidth, setActiveWidth] = useState(null); // Width of the dragged element
  const [activeHeight, setActiveHeight] = useState(null); // Height of the dragged element

  // Local state for items - this gets modified during drag for visual feedback
  const [localGroupedItems, setLocalGroupedItems] = useState(externalGroupedItems);

  // Track if we're currently dragging to avoid syncing external changes mid-drag
  const [isDragging, setIsDragging] = useState(false);

  // Track when the last drag ended to prevent immediate sync-back
  const lastDragEndRef = useRef(0);

  // Sync local state with external state ONLY when external data changes
  // Skip syncing if a drag just ended recently (prevents snap-back during position save)
  useEffect(() => {
    const timeSinceLastDrag = Date.now() - lastDragEndRef.current;
    const recentlyDragged = timeSinceLastDrag < 2000; // 2 second grace period

    if (!isDragging && !recentlyDragged) {
      setLocalGroupedItems(externalGroupedItems);
    }
  }, [externalGroupedItems, isDragging]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Custom collision detection that prioritizes items over category zones
  const collisionDetection = useCallback((args) => {
    // First check for intersections with items using closestCenter
    const closestCenterCollisions = closestCenter(args);

    if (closestCenterCollisions.length > 0) {
      return closestCenterCollisions;
    }

    // If no item collisions, check for category zone intersections
    const pointerCollisions = pointerWithin(args);
    return pointerCollisions;
  }, []);

  // Find which category contains an item
  const findContainer = useCallback((id) => {
    // Check if id is a category ID
    if (id?.toString().startsWith('category-')) {
      return id.replace('category-', '');
    }

    // Find item in local state
    for (const [category, items] of Object.entries(localGroupedItems)) {
      if (items.some(item => item.id === id)) {
        return category;
      }
    }
    return null;
  }, [localGroupedItems]);

  const handleDragStart = useCallback((event) => {
    const { active } = event;
    setActiveId(active.id);
    setActiveCategory(active.data.current?.category);
    setOverCategory(active.data.current?.category);
    setIsDragging(true);
    // Capture the dimensions of the dragged element
    if (active.rect.current.initial) {
      setActiveWidth(active.rect.current.initial.width);
      setActiveHeight(active.rect.current.initial.height);
    }
  }, []);

  const handleDragOver = useCallback((event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const overId = over.id;
    const activeContainer = findContainer(active.id);
    const overContainer = findContainer(overId);

    // Check for null/undefined specifically - empty string '' is valid (No Category)
    if (activeContainer == null || overContainer == null) {
      return;
    }

    setOverCategory(overContainer);

    setLocalGroupedItems((prev) => {
      const activeItems = [...(prev[activeContainer] || [])];
      const overItems = activeContainer === overContainer
        ? activeItems
        : [...(prev[overContainer] || [])];

      // Find the active item index
      const activeIndex = activeItems.findIndex(item => item.id === active.id);
      if (activeIndex === -1) return prev;

      // Find the over item index
      const overIndex = overItems.findIndex(item => item.id === overId);

      if (activeContainer === overContainer) {
        // Same container - reorder within the list
        if (activeIndex === overIndex) return prev;

        const newItems = [...activeItems];
        const [movedItem] = newItems.splice(activeIndex, 1);
        newItems.splice(overIndex, 0, movedItem);

        return {
          ...prev,
          [activeContainer]: newItems,
        };
      } else {
        // Different containers - move between lists
        const [movedItem] = activeItems.splice(activeIndex, 1);
        // Update the item's category to match the new container
        const updatedItem = { ...movedItem, category: overContainer === '' ? null : overContainer };
        const newIndex = overIndex >= 0 ? overIndex : overItems.length;
        overItems.splice(newIndex, 0, updatedItem);

        return {
          ...prev,
          [activeContainer]: activeItems,
          [overContainer]: overItems,
        };
      }
    });
  }, [findContainer]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    // Capture state values before clearing them
    const sourceCategory = activeCategory;
    const currentOverCategory = overCategory;

    // Record when drag ended to prevent immediate sync-back
    lastDragEndRef.current = Date.now();

    setActiveId(null);
    setActiveCategory(null);
    setOverCategory(null);
    setActiveWidth(null);
    setActiveHeight(null);
    setIsDragging(false);

    if (!over) {
      return;
    }

    // Determine target category from the drop target
    // Check if dropping on a category droppable zone (id like "category-news" or "category-")
    let targetCategory;
    if (over.id?.toString().startsWith('category-')) {
      targetCategory = over.id.replace('category-', '');
    } else {
      // Dropping on an item - use the item's category data or the tracked overCategory
      targetCategory = over.data.current?.category ?? currentOverCategory ?? sourceCategory;
    }

    // Get target category items from local state (reflects new visual order after drag)
    const targetItems = [...(localGroupedItems[targetCategory] || [])];

    // Find where the item currently is in the target container (new visual position)
    const activeIndex = targetItems.findIndex(item => item.id === active.id);
    const targetIndex = activeIndex >= 0 ? activeIndex : targetItems.length;

    // Get neighbors based on the NEW visual order (not sorted by old position)
    // The item before and after in the visual list are the neighbors we need
    const beforeItem = targetIndex > 0 ? targetItems[targetIndex - 1] : null;
    const afterItem = targetIndex < targetItems.length - 1 ? targetItems[targetIndex + 1] : null;

    // Skip the moved item itself when getting neighbors
    const actualBeforeItem = beforeItem?.id === active.id ? null : beforeItem;
    const actualAfterItem = afterItem?.id === active.id ? null : afterItem;

    // Calculate new position based on neighbors.
    // Use the same fallback (`m00000`) consumers use when sorting, so the
    // computed position is consistent with where unpositioned neighbors
    // actually sort. Using `zzzzzz` here would make the moved item sort
    // after unpositioned items instead of between them.
    const SORT_FALLBACK = 'm00000';
    let newPosition;
    if (!actualBeforeItem && !actualAfterItem) {
      // Empty category or only item
      newPosition = initialPosition();
    } else if (!actualBeforeItem) {
      // Inserting at start
      newPosition = positionBefore(actualAfterItem.position || SORT_FALLBACK);
    } else if (!actualAfterItem) {
      // Inserting at end
      newPosition = positionAfter(actualBeforeItem.position || SORT_FALLBACK);
    } else {
      // Inserting between two items
      const beforePos = actualBeforeItem.position || SORT_FALLBACK;
      const afterPos = actualAfterItem.position || SORT_FALLBACK;
      newPosition = positionBetween(beforePos, afterPos);
      if (!newPosition) {
        // No room between neighbors (or both share the same key): nudge after
        newPosition = positionAfter(beforePos);
      }
    }

    // Keep local state in sync with the persisted position so subsequent drags
    // within the grace period see fresh `position` values on the moved item.
    setLocalGroupedItems((prev) => {
      const target = prev[targetCategory];
      if (!target) return prev;
      return {
        ...prev,
        [targetCategory]: target.map((item) =>
          item.id === active.id
            ? { ...item, position: newPosition, category: targetCategory === '' ? null : targetCategory }
            : item
        ),
      };
    });

    if (sourceCategory === targetCategory) {
      // Same category reorder
      if (onReorder) {
        onReorder({
          itemId: active.id,
          sourceCategory,
          targetCategory,
          newPosition,
          targetIndex,
          beforeId: beforeItem?.id,
          afterId: afterItem?.id,
        });
      }
    } else {
      // Cross-category move
      if (onCategoryChange) {
        onCategoryChange({
          itemId: active.id,
          sourceCategory,
          targetCategory,
          newPosition,
          newCategory: targetCategory, // Keep as empty string for "No Category"
        });
      }
    }
  }, [activeCategory, localGroupedItems, findContainer, onReorder, onCategoryChange]);

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
    setActiveCategory(null);
    setOverCategory(null);
    setActiveWidth(null);
    setActiveHeight(null);
    setIsDragging(false);
    // Reset to external state on cancel
    setLocalGroupedItems(externalGroupedItems);
  }, [externalGroupedItems]);

  // DndContext props to spread
  const dndContextProps = {
    sensors,
    collisionDetection,
    onDragStart: handleDragStart,
    onDragOver: handleDragOver,
    onDragEnd: handleDragEnd,
    onDragCancel: handleDragCancel,
    modifiers: [restrictToVerticalAxis],
  };

  // Find the active item for DragOverlay (from local state to track during drag)
  const activeItem = activeId
    ? Object.values(localGroupedItems).flat().find(item => item.id === activeId)
    : null;

  return {
    activeId,
    activeItem,
    activeWidth,
    activeHeight,
    activeCategory,
    overCategory,
    isDragging,
    // Return local grouped items - this reflects drag state for visual feedback
    groupedItems: localGroupedItems,
    dndContextProps,
    DndContext,
    SortableItem,
    SortableCategory,
    DroppableCategory,
  };
}

export default useSortableArticles;
